import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useRouter } from '../RouterContext';

const CookiesPage: React.FC = () => {
  const { navigate } = useRouter();

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={s.topbarInner}>
          <button onClick={() => navigate('home')} style={s.back}>
            <ArrowLeft size={16} />
            Voltar
          </button>
          <span style={s.logoMark}>2GF</span>
        </div>
      </div>

      <div style={s.container}>
        <div style={s.header}>
          <span style={s.label}>Informação sobre Cookies</span>
          <h1 style={s.title}>Política de Cookies</h1>
          <p style={s.lastUpdate}>Última atualização: Janeiro de 2026</p>
        </div>

        <div style={s.content}>
          <p style={s.intro}>
            Esta Política de Cookies explica o que são cookies, como os utilizamos no nosso website
            www.2gfinnovationsystems.pt e quais as opções que tem relativamente à sua utilização.
          </p>

          <Section title="1. O que São Cookies?">
            <p style={s.p}>
              Cookies são pequenos ficheiros de texto colocados no seu dispositivo quando visita um website.
              São amplamente utilizados para fazer os websites funcionarem de forma eficiente e para fornecer
              informações aos proprietários do site.
            </p>
            <p style={s.p}>
              Os cookies podem ser <strong>persistentes</strong> (permanecem no seu dispositivo depois de fechar o
              navegador) ou <strong>de sessão</strong> (temporários, expiram quando fecha o navegador).
            </p>
          </Section>

          <Section title="2. Como Utilizamos os Cookies?">
            <ul style={s.ul}>
              <li><strong>Cookies Essenciais</strong> — necessários para o funcionamento básico do website</li>
              <li><strong>Cookies de Desempenho</strong> — ajudam-nos a compreender como os visitantes interagem com o site</li>
              <li><strong>Cookies de Funcionalidade</strong> — permitem que o website se lembre das suas escolhas</li>
              <li><strong>Cookies de Marketing</strong> — utilizados para apresentar anúncios relevantes</li>
            </ul>
            <p style={s.p}>
              Não utilizamos cookies para recolher informação pessoalmente identificável sem o seu consentimento explícito.
            </p>
          </Section>

          <Section title="3. Tipos de Cookies que Utilizamos">
            <SubSection title="3.1 Cookies Estritamente Necessários">
              <p style={s.p}>
                Essenciais para o funcionamento do website. Não podem ser desativados. São definidos em resposta
                a ações realizadas por si, como definir preferências de privacidade ou preencher formulários.
              </p>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Cookie</th>
                    <th style={s.th}>Finalidade</th>
                    <th style={s.th}>Duração</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={s.td}>cookieyes-consent</td>
                    <td style={s.td}>Armazena o consentimento do utilizador</td>
                    <td style={s.td}>1 ano</td>
                  </tr>
                  <tr>
                    <td style={s.td}>session_id</td>
                    <td style={s.td}>Mantém a sessão ativa</td>
                    <td style={s.td}>Sessão</td>
                  </tr>
                </tbody>
              </table>
            </SubSection>

            <SubSection title="3.2 Cookies de Desempenho e Análise">
              <p style={s.p}>
                Permitem contar visitas e fontes de tráfego para medir e melhorar o desempenho do website.
              </p>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Cookie</th>
                    <th style={s.th}>Fornecedor</th>
                    <th style={s.th}>Finalidade</th>
                    <th style={s.th}>Duração</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={s.td}>_ga</td>
                    <td style={s.td}>Google Analytics</td>
                    <td style={s.td}>Distingue utilizadores únicos</td>
                    <td style={s.td}>2 anos</td>
                  </tr>
                  <tr>
                    <td style={s.td}>_gid</td>
                    <td style={s.td}>Google Analytics</td>
                    <td style={s.td}>Distingue utilizadores</td>
                    <td style={s.td}>24 horas</td>
                  </tr>
                  <tr>
                    <td style={s.td}>_gat</td>
                    <td style={s.td}>Google Analytics</td>
                    <td style={s.td}>Limita a taxa de pedidos</td>
                    <td style={s.td}>1 minuto</td>
                  </tr>
                </tbody>
              </table>
            </SubSection>

            <SubSection title="3.3 Cookies de Funcionalidade">
              <p style={s.p}>
                Permitem que o website forneça funcionalidade e personalização melhoradas. Podem ser definidos
                por nós ou por fornecedores terceiros cujos serviços integrámos.
              </p>
            </SubSection>

            <SubSection title="3.4 Cookies de Marketing e Publicidade">
              <p style={s.p}>
                Podem ser definidos pelos nossos parceiros de publicidade para construir um perfil dos seus interesses
                e mostrar anúncios relevantes noutros websites.
              </p>
            </SubSection>
          </Section>

          <Section title="4. Cookies de Terceiros">
            <p style={s.p}>Utilizamos serviços de terceiros que também podem definir cookies:</p>
            <ul style={s.ul}>
              <li><strong>Google Analytics</strong> — análise de tráfego e comportamento dos visitantes</li>
              <li><strong>Tawk.to</strong> — funcionalidade de chat ao vivo</li>
              <li><strong>Google Fonts</strong> — tipografia do website</li>
              <li><strong>Font Awesome</strong> — ícones e elementos visuais</li>
            </ul>
            <p style={s.p}>
              Estes serviços têm as suas próprias políticas de privacidade e cookies, sobre as quais não temos controlo.
            </p>
          </Section>

          <Section title="5. Como Controlar e Gerir Cookies">
            <SubSection title="5.1 Configurações do Navegador">
              <p style={s.p}>
                Pode controlar e eliminar cookies através das configurações do seu navegador:
              </p>
              <ul style={s.ul}>
                <li>Chrome: support.google.com/chrome/answer/95647</li>
                <li>Firefox: support.mozilla.org/pt-PT/kb/cookies</li>
                <li>Safari: support.apple.com/pt-pt/guide/safari/sfri11471/mac</li>
                <li>Edge: support.microsoft.com/pt-pt/microsoft-edge</li>
              </ul>
            </SubSection>
            <SubSection title="5.2 Impacto da Desativação">
              <p style={s.p}>
                Se desativar os cookies, algumas partes do nosso website podem não funcionar corretamente e
                algumas funcionalidades podem não estar disponíveis.
              </p>
            </SubSection>
          </Section>

          <Section title="6. Cookies e Dados Pessoais">
            <p style={s.p}>
              Alguns cookies podem recolher informação que pode ser considerada dados pessoais. Para mais
              informações, consulte a nossa <button onClick={() => navigate('privacy')} style={s.inlineLink}>Política de Privacidade</button>.
            </p>
          </Section>

          <Section title="7. Atualizações desta Política">
            <p style={s.p}>
              Podemos atualizar esta Política de Cookies periodicamente para refletir alterações nas nossas
              práticas ou por razões operacionais, legais ou regulamentares. Recomendamos que consulte esta
              página regularmente.
            </p>
          </Section>

          <Section title="8. Contacte-nos">
            <p style={s.p}>
              2GF Innovation Systems<br />
              Email: <a href="mailto:digital@2gfinnovationsystems.pt" style={s.a}>digital@2gfinnovationsystems.pt</a><br />
              Telefone: +351 969 059 898<br />
              Morada: Centro de Inovação Empresarial da Covilhã, R. António Augusto de Aguiar, 6200-053 Covilhã, Portugal<br />
              NIF: 516285858
            </p>
          </Section>
        </div>
      </div>

      <div style={s.footer}>
        <span>© {new Date().getFullYear()} 2GF Innovation Systems · NIF 516285858</span>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={s.section}>
    <h2 style={s.sectionTitle}>{title}</h2>
    {children}
  </div>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={s.subsection}>
    <h3 style={s.subsectionTitle}>{title}</h3>
    {children}
  </div>
);

const s: { [key: string]: React.CSSProperties } = {
  page: {
    background: '#ffffff',
    minHeight: '100vh',
  },
  topbar: {
    background: '#020F1E',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '1rem 0',
  },
  topbarInner: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  back: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    letterSpacing: '0.05em',
    padding: 0,
  },
  logoMark: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.25rem',
    fontWeight: 900,
    color: '#0057FF',
    letterSpacing: '-0.02em',
  },
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '4rem 2rem 6rem',
  },
  header: {
    marginBottom: '3rem',
    paddingBottom: '3rem',
    borderBottom: '1px solid #E8EDF3',
  },
  label: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.2em',
    textTransform: 'uppercase' as const,
    color: '#0057FF',
    display: 'block',
    marginBottom: '0.75rem',
  },
  title: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(2rem, 4vw, 2.75rem)',
    fontWeight: 800,
    color: '#0D1B2A',
    letterSpacing: '-0.03em',
    lineHeight: 1.1,
    marginBottom: '0.75rem',
  },
  lastUpdate: {
    fontSize: '0.875rem',
    color: '#94A3B8',
    margin: 0,
  },
  intro: {
    fontSize: '1rem',
    color: '#64748B',
    lineHeight: 1.85,
    marginBottom: '3rem',
    paddingBottom: '3rem',
    borderBottom: '1px solid #E8EDF3',
  },
  content: {},
  section: {
    marginBottom: '2.5rem',
    paddingBottom: '2.5rem',
    borderBottom: '1px solid #F1F5F9',
  },
  sectionTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#0D1B2A',
    letterSpacing: '-0.01em',
    marginBottom: '1rem',
  },
  subsection: {
    marginTop: '1.25rem',
  },
  subsectionTitle: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
  },
  p: {
    fontSize: '0.9rem',
    color: '#64748B',
    lineHeight: 1.85,
    marginBottom: '0.75rem',
  },
  ul: {
    fontSize: '0.9rem',
    color: '#64748B',
    lineHeight: 1.85,
    paddingLeft: '1.25rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
    marginTop: '0.75rem',
  },
  th: {
    textAlign: 'left' as const,
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: '#94A3B8',
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #E8EDF3',
  },
  td: {
    color: '#64748B',
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #F1F5F9',
    verticalAlign: 'top' as const,
  },
  a: {
    color: '#0057FF',
    textDecoration: 'none',
  },
  inlineLink: {
    color: '#0057FF',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    textDecoration: 'underline',
  },
  footer: {
    background: '#020F1E',
    padding: '1.5rem 2rem',
    textAlign: 'center' as const,
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.25)',
  },
};

export default CookiesPage;
